import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";

type AuthState =
  | { status: "checking" }
  | { status: "logged_out"; error?: string }
  | { status: "logged_in" };

type AuthContextValue = {
  state: AuthState;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "checking" });

  useEffect(() => {
    let alive = true;
    api
      .listSavedSearches()
      .then(() => alive && setState({ status: "logged_in" }))
      .catch((e) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 401) {
          setState({ status: "logged_out" });
        } else {
          setState({ status: "logged_out", error: String(e) });
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  async function login(password: string) {
    setState({ status: "checking" });
    try {
      await api.login(password);
      setState({ status: "logged_in" });
    } catch (e) {
      const error =
        e instanceof ApiError && e.status === 401 ? "wrong password" : String(e);
      setState({ status: "logged_out", error });
    }
  }

  async function logout() {
    try {
      await api.logout();
    } finally {
      setState({ status: "logged_out" });
    }
  }

  return (
    <AuthContext.Provider value={{ state, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
